import ContactForm from "@/components/ContactForm";
import FormCard from "@/components/FormCard";
import { SectionHeader } from "@/components/ui/SectionHeader";
import { Phone, Mail, MapPin } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";

export default function ContactSection() {
  const { t } = useTranslation('home');
  const [statsVisible, setStatsVisible] = useState(false);
  const statsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const statsObserver = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setStatsVisible(true);
      },
      { threshold: 0.3 }
    );
    if (statsRef.current) statsObserver.observe(statsRef.current);
    return () => statsObserver.disconnect();
  }, []);

  const stats = [
    { value: "7,000+", label: t('contact.stats.roofsByCrew') },
    { value: "15+", label: t('contact.stats.yearsInHouston') },
    { value: "24/7", label: t('contact.stats.emergencyService') },
  ];

  return (
    <section id="contact" className="bg-white py-24 md:py-32">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <SectionHeader
          eyebrow={t('contact.eyebrow')}
          headline={t('contact.headline')}
          subheadline={t('contact.subheadline')}
          className="mb-16"
        />

        <div className="grid md:grid-cols-2 gap-12 items-start">
          {/* Contact Information */}
          <div className="space-y-10">
            {/* Trust Indicators - static stats, left-aligned */}
            <div ref={statsRef} className="flex flex-wrap gap-12">
              {stats.map((stat, i) => (
                <div
                  key={stat.label}
                  className={`reveal-up ${statsVisible ? "is-visible" : ""}`}
                  style={{ transitionDelay: `${i * 100}ms` }}
                >
                  <div className="font-heading text-4xl md:text-5xl font-bold text-primary leading-none tabular-nums">
                    {stat.value}
                  </div>
                  <div className="w-8 h-0.5 bg-primary mt-2 mb-1" />
                  <div className="text-sm text-muted-foreground">{stat.label}</div>
                </div>
              ))}
            </div>

            <div>
              <h3 className="font-heading uppercase text-navy text-2xl mb-6">{t('contact.info.title')}</h3>
              <div className="space-y-4">
                <motion.div
                  className="flex items-start gap-4"
                  whileHover={{ x: 4 }}
                  transition={{ duration: 0.2 }}
                >
                  <Phone className="h-6 w-6 text-primary flex-shrink-0" strokeWidth={2.5} />
                  <div>
                    <h4 className="font-heading uppercase text-navy mb-1">{t('contact.info.phone')}</h4>
                    <a href="tel:+13462200989" className="text-muted-foreground hover:text-primary transition-colors">
                      (346) 220-0989
                    </a>
                  </div>
                </motion.div>

                <motion.div
                  className="flex items-start gap-4"
                  whileHover={{ x: 4 }}
                  transition={{ duration: 0.2 }}
                >
                  <Mail className="h-6 w-6 text-primary flex-shrink-0" strokeWidth={2.5} />
                  <div>
                    <h4 className="font-heading uppercase text-navy mb-1">{t('contact.info.email')}</h4>
                    <a href="mailto:Contact@altumcontractors.com" className="text-muted-foreground hover:text-primary transition-colors">
                      Contact@altumcontractors.com
                    </a>
                  </div>
                </motion.div>

                <motion.div
                  className="flex items-start gap-4"
                  whileHover={{ x: 4 }}
                  transition={{ duration: 0.2 }}
                >
                  <MapPin className="h-6 w-6 text-primary flex-shrink-0" strokeWidth={2.5} />
                  <div>
                    <h4 className="font-heading uppercase text-navy mb-1">{t('contact.info.serviceArea')}</h4>
                    <p className="text-muted-foreground">
                      {t('contact.info.serviceAreaText')}
                    </p>
                  </div>
                </motion.div>
              </div>
            </div>

            <div className="border-t border-border pt-4">
              <h4 className="font-heading uppercase text-navy mb-2">{t('contact.info.hours')}</h4>
              <p className="text-sm text-muted-foreground">
                {t('contact.info.hoursWeekday')}<br />
                {t('contact.info.hoursSaturday')}<br />
                {t('contact.info.hoursSunday')}<br />
                <span className="text-primary font-medium">{t('contact.info.emergency247')}</span>
              </p>
            </div>
          </div>

          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.2, ease: [0.25, 0.46, 0.45, 0.94] }}
          >
            <FormCard title={t('contact.formTitle')}>
              <ContactForm />
            </FormCard>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
